import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Check, CheckCircle2, ExternalLink, Link2, MoreHorizontal, Upload } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { problemCode } from '@up2d8/api-client';
import type { FeedPage, MyReachStatusList } from '@up2d8/contracts';
import {
  AttachmentRowBody,
  Badge,
  PostHead,
  Button,
  Card,
  attachmentRowClass,
  cn,
  EmptyState,
  LoadingState,
  fileSizeLabel,
  relativeTime,
  useToast,
} from '@up2d8/ui';
import {
  LAST_SYNC_KEY,
  cachedFeed,
  cachedStatuses,
  saveFeedItems,
  saveStatuses,
  setMeta,
} from '../../offline/db';
import { enqueue } from '../../offline/outbox';
import { HANDLES_OFFLINE } from '../../offline/network-mode';
import { useOffline } from '../../offline/use-offline';
import { ListError } from '../../shared/list-error';
import { useSession } from '../../shared/session';
import {
  CommentPreviewStrip,
  InteractionBar,
  inBatches,
  useCommentPreviews,
  useFeedCounts,
  useLikes,
} from './app-interaction';

/**
 * De berichten van de medewerker, nieuwste eerst.
 *
 * Kaarten met een titel, het begin van de tekst en wat erbij hoort. Niet de volledige tekst:
 * op een telefoon met een trage verbinding is een lijst van twintig volledige berichten het
 * verschil tussen een seconde en tien, en op een werkvloer scrolt niemand door drie alinea's
 * om te zien of er iets voor hem bij staat.
 *
 * "Meer laden" en geen oneindig scrollen: bij een lijst die vanzelf doorlaadt, weet je nooit
 * of je aan het einde bent.
 */
/**
 * Het "…"-menu op een berichtkaart (replica-spec §9.1).
 *
 * Twee handelingen, en meer niet: het bericht openen, en de link kopièren. De mockup suggereert er
 * meer — melden, bewaren — maar een menu dat opengaat met handelingen die niets doen, is erger dan
 * geen menu. Wat erbij komt, komt erbij met de module die het kan.
 *
 * `<details>` en niet een eigen openklapmechaniek met `useEffect` en een klik op het document: de
 * browser doet het sluiten bij Escape en het aankondigen aan een schermlezer al, en op een telefoon
 * werkt het onder een vinger zonder dat er iets te luisteren valt.
 */
function PostMenu({ postId }: { postId: string }) {
  const { t } = useTranslation();
  const toast = useToast();

  const link = `${window.location.origin}/app/posts/${postId}`;

  /*
    `<details>` zonder `open` in de state, en dat is een correctie.

    De eerste versie hield `open` in React bij en zette het attribuut terug: dan vecht React met de
    browser, die het attribuut zelf al omzet bij een klik, en bleef het menu dicht. Gevonden door
    de e2e-test die het menu opent en de twee handelingen zoekt — de regels stónden in de DOM, maar
    onzichtbaar.

    Uncontrolled dus. Het sluiten na "link kopiêren" gebeurt op het element zelf, want dat is het
    enige moment waarop de app het menu wil dichtdoen.
  */
  return (
    <details className="relative shrink-0">
      <summary
        data-testid="post-menu"
        aria-label={t('common.actions')}
        className="flex h-8 w-8 cursor-pointer list-none items-center justify-center rounded-pill text-text-muted transition-colors marker:hidden hover:bg-background hover:text-text-primary [&::-webkit-details-marker]:hidden"
      >
        <MoreHorizontal aria-hidden className="h-4 w-4" />
      </summary>

      <div className="absolute right-0 z-10 mt-1 w-48 overflow-hidden rounded-md border border-border bg-surface py-1 shadow-lg">
        <Link
          to="/app/posts/$postId"
          params={{ postId }}
          className="flex items-center gap-2 px-3 py-2 text-small text-text-primary hover:bg-background"
        >
          <ExternalLink aria-hidden className="h-3.5 w-3.5 text-text-muted" />
          {t('feed.menuOpen')}
        </Link>
        <button
          type="button"
          data-testid="post-menu-copy"
          onClick={(event) => {
            event.currentTarget.closest('details')?.removeAttribute('open');
            void navigator.clipboard
              .writeText(link)
              .then(() => toast.show({ tone: 'success', title: t('feed.menuCopied') }))
              // Zonder beveiligde verbinding bestaat het klembord niet. Dan hoort de app te zeggen
              // dat het niet lukte, en niet te doen alsof.
              .catch(() => toast.show({ tone: 'danger', title: t('feed.menuCopyFailed') }));
          }}
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-small text-text-primary hover:bg-background"
        >
          <Link2 aria-hidden className="h-3.5 w-3.5 text-text-muted" />
          {t('feed.menuCopy')}
        </button>
      </div>
    </details>
  );
}

export function AppFeed() {
  const { t } = useTranslation();
  const { api } = useSession();

  const { pending, online, flush, refreshPending } = useOffline();
  const queryClient = useQueryClient();
  const toast = useToast();

  /**
   * De feed, met de lokale kopie als vangnet (Stap 12).
   *
   * Eerst de server, en bij een mislukking wat er op het toestel staat. Die orde is belangrijk: de
   * cache is een vangnet en geen bron, dus wie bereik heeft, ziet altijd de stand van de server.
   *
   * De eerste pagina wordt bewaard, de volgende niet. Wie in een hal zonder dekking doorbladert naar
   * drie maanden terug, doet iets anders dan waar deze app voor is — en vijftig berichten is de grens
   * die de opslag van een telefoon aankan.
   */
  const feed = useInfiniteQuery({
    queryKey: ['feed'],
    // Offline valt de functie zelf terug op de kopie; gepauzeerd zou ze dat nooit doen.
    ...HANDLES_OFFLINE,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      let answered = false;
      try {
        const { data, error, response } = await api.GET('/api/v1/feed', {
          params: { query: { limit: 10, ...(pageParam ? { cursor: pageParam } : {}) } },
        });
        answered = response !== undefined;
        if (error || !data) throw error ?? new Error('Ophalen mislukt');

        const page = data as FeedPage;
        if (!pageParam) {
          await saveFeedItems(page.items);
          await setMeta(LAST_SYNC_KEY, new Date().toISOString());
        }
        return page;
      } catch (error) {
        // Alleen zonder antwoord van de server terugvallen op de kopie. Heeft de server geantwoord —
        // een `401` bij een verlopen sessie, een `403` — dan hoort dat antwoord te gelden.
        if (answered || pageParam) throw error;
        const local = await cachedFeed();
        if (local.length === 0) throw error;
        return { items: local, nextCursor: null } satisfies FeedPage;
      }
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = feed.data?.pages.flatMap((page) => page.items) ?? [];

  /**
   * Mijn eigen stand bij de berichten op deze pagina, in één aanroep.
   *
   * Zo verandert de badge "Bevestigen" in "Bevestigd" zodra je het gedaan hebt — anders blijft de
   * feed om iets vragen wat je net gedaan hebt, en dat is precies waarop mensen afhaken.
   */
  const statuses = useQuery({
    queryKey: ['my-reach', items.map((item) => item.postId)],
    enabled: items.length > 0,
    ...HANDLES_OFFLINE,
    queryFn: async () => {
      try {
        // In brokken van vijftig, het maximum van de server: wie verder scrolde, stuurde anders
        // alle ids in één keer, kreeg een 400 en zag elke "Bevestigd" terugvallen.
        const rows: MyReachStatusList['items'] = [];
        for (const batch of inBatches(items.map((item) => item.postId))) {
          const { data, error, response } = await api.GET('/api/v1/feed/my-statuses', {
            params: { query: { postIds: batch.join(',') } },
          });

          // Mét antwoord geldt het antwoord; zonder antwoord de standen van de vorige keer. Zonder
          // dat laatste zou de feed offline bij elk bericht opnieuw om een bevestiging vragen die
          // de medewerker al gaf.
          if (error) return response !== undefined ? [] : cachedStatuses();
          rows.push(...((data as MyReachStatusList | undefined)?.items ?? []));
        }

        await saveStatuses(rows);
        return rows;
      } catch {
        return cachedStatuses();
      }
    },
  });

  const acknowledged = new Set(
    (statuses.data ?? []).filter((row) => row.acknowledgedAt !== null).map((row) => row.postId),
  );

  /**
   * De tellers voor deze pagina: likes uit `interaction`, weergaven uit `reach` (Stap 12b).
   *
   * Twee aanroepen en niet één, omdat het twee modules zijn. Voor de lezer is het één rij onder de
   * kaart; het verschil tussen "wat mensen deden" en "wat we meten" zit in wie de vraag beantwoordt.
   */
  const postIds = items.map((item) => item.postId);
  const likes = useLikes(postIds);
  const counts = useFeedCounts(postIds);
  // Sinds Extra 5 komt het aantal mét de laatste drie reacties mee: één aanroep in plaats van
  // een teller nu en een strook later.
  const commentPreviews = useCommentPreviews(postIds);

  const likeByPost = new Map((likes.data ?? []).map((row) => [row.postId, row]));
  const countsByPost = new Map((counts.data ?? []).map((row) => [row.postId, row]));
  const previewByPost = new Map((commentPreviews.data ?? []).map((row) => [row.postId, row]));

  /**
   * Bevestigen vanaf de kaart, zonder het bericht te openen (replica-spec §9.4).
   *
   * Dezelfde weg als op de berichtpagina, met één verschil dat ertoe doet: de versie en de taal
   * komen hier uit het feed-item en niet uit het gelezen bericht. Dat is geen afronding — het bewijs
   * moet zeggen wélke tekst iemand bevestigde (ADR-0042), en de kaart weet dat van zichzelf.
   *
   * Een antwoord van de server is een oordeel en gaat niet in de wachtrij: bij een `409` moet hij
   * de nieuwe tekst lezen. Alleen een verbinding die er niet is, is een reden om te wachten.
   */
  const acknowledge = useMutation({
    // Offline niet laten pauzeren: zonder netwerk gaat de bevestiging in de wachtrij.
    ...HANDLES_OFFLINE,
    mutationFn: async (item: {
      postId: string;
      version: number;
      languageShown: string;
    }): Promise<'verzonden' | 'in-wachtrij'> => {
      const occurredAt = new Date().toISOString();
      const payload = {
        postVersion: item.version,
        languageShown: item.languageShown,
        clientOccurredAt: occurredAt,
      };

      try {
        const { error } = await api.POST('/api/v1/feed/{postId}/acknowledge', {
          params: { path: { postId: item.postId } },
          body: payload as never,
        });
        if (error) throw error;
        return 'verzonden';
      } catch (error) {
        const code = problemCode(error);
        if (code === 'CONFLICT' || code === 'CLIENT_TIME_REJECTED') throw error;

        await enqueue({ kind: 'acknowledge', postId: item.postId, ...payload });
        return 'in-wachtrij';
      }
    },
    onSuccess: async (result) => {
      await refreshPending();
      await queryClient.invalidateQueries({ queryKey: ['my-reach'] });
      await queryClient.invalidateQueries({ queryKey: ['feed-counts'] });
      // "Voor jou" toont openstaande bevestigingen; zonder dit bleef het bericht daar staan.
      await queryClient.invalidateQueries({ queryKey: ['open-items'] });
      toast.show({
        title: result === 'verzonden' ? t('acknowledge.done') : t('offline.acknowledgeQueued'),
        tone: 'success',
      });
      if (result === 'in-wachtrij' && online) void flush();
    },
    onError: (error) =>
      toast.show({
        title:
          problemCode(error) === 'CONFLICT' ? t('acknowledge.changed') : t('acknowledge.failed'),
        tone: 'danger',
      }),
  });

  if (feed.isPending) return <LoadingState rows={3} label={t('common.loading')} />;

  // Een mislukte feed is geen lege feed: anders leest "de server was er even niet" als "er is
  // niets voor jou". Lukte alleen een volgende pagina niet, dan blijven de geladen kaarten staan.
  if (feed.isError && items.length === 0) {
    return <ListError onRetry={() => void feed.refetch()} error={feed.error} />;
  }

  if (items.length === 0) {
    return (
      <Card>
        <EmptyState title={t('appHome.emptyFeed')} description={t('appHome.emptyFeedHint')} />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-element">
      {items.map((item) => (
        /*
          De berichtkaart (replica-spec §9), blok voor blok met de paddings uit de referentie.

          De kaart is géén link meer in zijn geheel: de tellerrij bevat een echte knop, en een
          button binnen een anchor is ongeldige HTML die bij elke tik ook navigeert. Daarom staan er
          drie links in één kaart — kop, bevestigen, bijlage — en de tellerrij ernaast. Tikken op de
          titel opent het bericht; tikken op het hartje doet alleen dat.

          `p-0` op de kaart: de blokken hieronder dragen hun eigen padding, want die verschilt per
          blok (20 px links en rechts voor de tekst, 14 px voor de voet). Één padding op de kaart
          zou de voetrand laten inspringen, en dan loopt de scheidingslijn niet door.
        */
        <Card
          key={item.postId}
          data-testid="feed-card"
          data-post-id={item.postId}
          className="p-0 transition-colors focus-within:border-brand-primary hover:border-brand-primary"
        >
          {/*
            De afzenderregel (§9.1): avatar 44, naam met afdelingschip, daaronder tijd en doelgroep.

            Dit is de vraag die een operator als eerste stelt — komt dit van HR, van de
            preventiedienst of van mijn eigen ploegleider — en ze bepaalt of hij nu leest of straks.
            Een bericht zonder afzender (uit de seed of een import) laat de naam weg; "Onbekend" op
            het scherm van een operator is erger dan niets.

            De naam van de **afzender** mag hier staan, die van een ontvanger niet (ADR-0014); de
            doelgroep staat er als omschrijving ("Iedereen", "Ploeg Nacht"), nooit als lijst namen.

            Deze regel staat **buiten** de link naar het bericht, en dat is een correctie: het
            "…"-menu is een `<details>`, en een `<details>` binnen een `<a>` is ongeldige HTML. De
            browser herschikte de boom, het menu ging niet open, en elke tik erop navigeerde. De
            titel en de tekst hieronder zijn de link — dat is ook waar een lezer tikt.
          */}
          <PostHead
            author={item.author}
            time={relativeTime(item.publishedAt, new Date(), {
              justNow: t('feed.justNow'),
              minutesAgo: t('feed.minutesAgo'),
              hoursAgo: t('feed.hoursAgo'),
              yesterday: t('feed.yesterday'),
            })}
            audience={item.audienceDescription}
            right={
              <>
                {item.requiresAcknowledgement &&
                  // Wat nog in de wachtrij staat, is voor de medewerker gedaan: hij drukte op
                  // de knop. Een badge "Bevestigen" daarbij zou vragen om iets wat hij al deed.
                  // En staat het nog open, dan zegt de balk hieronder het al — twee keer
                  // dezelfde vraag op één kaart maakt de tweede niet dringender.
                  (pending.has(item.postId) ? (
                    <Badge tone="neutral">
                      <Upload aria-hidden className="mr-1 inline h-3.5 w-3.5" />
                      {t('offline.waitingToSend')}
                    </Badge>
                  ) : acknowledged.has(item.postId) ? (
                    <Badge tone="success">
                      <CheckCircle2 aria-hidden className="mr-1 inline h-3.5 w-3.5" />
                      {t('feed.acknowledged')}
                    </Badge>
                  ) : null)}
                <PostMenu postId={item.postId} />
              </>
            }
          />

          <Link
            to="/app/posts/$postId"
            params={{ postId: item.postId }}
            className="block focus-visible:outline-none"
          >
            {/*
              `text-card-title` en `text-copy`, de maten met een rol — niet de vaste 15 px en 13 px
              uit de spec.

              Het verschil is onzichtbaar op een bureauscherm, want daar staan die rollen al op 15
              en 13. Op een telefoon niet: daar tilt de werkvloervloer ze naar 17 en 16 (ADR-0065,
              aanvulling van 5 oktober 2026). Met de vaste maten stond de titel van een
              veiligheidsinstructie op een telefoon op 15 px, en daar is die vloer juist voor.
            */}
            <h2
              data-testid="feed-title"
              className="px-5 pb-1.5 font-heading text-card-title font-semibold leading-[1.4] text-text-primary"
            >
              {item.title}
            </h2>

            <p className="line-clamp-3 px-5 pb-3.5 text-copy leading-[1.55] text-text-secondary">
              {item.excerpt}
            </p>
          </Link>

          {/*
            De bevestigingsbalk (§9.4), en alleen zolang er iets te bevestigen valt.

            Zónder datum: een bevestigingsdeadline bestaat niet in ons model. De mockup zet er "voor
            30/09" bij; dat zou hier een verzonnen datum zijn, en een verzonnen deadline op een
            veiligheidsinstructie is erger dan geen (besluit 5 oktober 2026, zie deviations.md).
          */}
          {item.requiresAcknowledgement &&
          !acknowledged.has(item.postId) &&
          !pending.has(item.postId) ? (
            <div
              data-testid="feed-ack-bar"
              className="mx-5 mb-3.5 flex items-center gap-3 rounded-md border border-brand-orange-soft bg-gradient-to-r from-brand-orange-tint to-transparent px-3.5 py-3"
            >
              <span
                aria-hidden
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-brand-orange text-text-on-dark"
              >
                <Check className="h-4 w-4" strokeWidth={2.5} />
              </span>
              <p className="flex-1 text-small leading-[1.4] text-text-secondary">
                <strong className="font-heading font-semibold text-brand-orange-deep">
                  {t('feed.ackRequired')}
                </strong>{' '}
                {'\u2014 ' + t('feed.ackRequiredHint')}
              </p>
              <button
                type="button"
                data-testid="feed-ack-button"
                disabled={acknowledge.isPending}
                onClick={() =>
                  acknowledge.mutate({
                    postId: item.postId,
                    version: item.version,
                    languageShown: item.languageShown,
                  })
                }
                className="flex shrink-0 items-center justify-center rounded-pill bg-brand-orange px-3.5 py-[7px] font-heading text-small font-semibold text-text-on-dark transition-colors hover:bg-brand-orange-deep disabled:opacity-60"
              >
                {t('feed.ackButton')}
              </button>
            </div>
          ) : null}

          {/*
            De bijlagerij (§9.5): een vierkant met het soort, de naam en de maat.

            Wat hier staat is het aantal en niet de bestandsnaam: de feed draagt alleen een teller
            mee, en één extra aanroep per kaart om een naam te halen is een aanroep per kaart. De
            paginatelling uit de mockup (acht bladzijden) blijft weg — die staat nergens in ons model.
          */}
          {item.attachmentCount > 0 ? (
            <Link
              to="/app/posts/$postId"
              params={{ postId: item.postId }}
              data-testid="feed-attachment"
              className={cn('mx-5 mb-3.5', attachmentRowClass)}
            >
              <AttachmentRowBody
                name={item.attachment?.name ?? t('feed.attachments')}
                isImage={item.hasImage}
                meta={[
                  item.attachment ? fileSizeLabel(item.attachment.sizeBytes) : null,
                  item.attachmentCount > 1
                    ? t('feed.moreAttachments', { count: item.attachmentCount - 1 })
                    : null,
                ]
                  .filter(Boolean)
                  .join(' \u00b7 ')}
              />
            </Link>
          ) : null}

          {/* De voet (§9.6): een doorlopende scheidingslijn, en daaronder de tellers. */}
          <div data-testid="feed-footer" className="border-t border-border-light px-3.5 py-2">
            <InteractionBar
              postId={item.postId}
              like={likeByPost.get(item.postId)}
              viewCount={countsByPost.get(item.postId)?.viewCount ?? null}
              commentCount={previewByPost.get(item.postId)?.count ?? null}
              suppressed={countsByPost.get(item.postId)?.suppressed ?? false}
            />
            <CommentPreviewStrip postId={item.postId} group={previewByPost.get(item.postId)} />
          </div>
        </Card>
      ))}

      {feed.hasNextPage && (
        <Button
          variant="secondary"
          loading={feed.isFetchingNextPage}
          onClick={() => void feed.fetchNextPage()}
        >
          {t('feed.more')}
        </Button>
      )}
    </div>
  );
}
